import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PasswordInput } from '../components/common';

const css = `
  * { box-sizing: border-box; }
  .reg-page { min-height: 100vh; display: flex; align-items: flex-start; justify-content: center; background: #f5f5f7; padding: 24px 16px; }
  .reg-card { background: #fff; border-radius: 16px; padding: 40px; width: 100%; max-width: 500px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
  .reg-fld { display: flex; flex-direction: column; gap: 6px; }
  .reg-lbl { font-size: 13px; font-weight: 500; color: #374151; }
  .reg-inp { padding: 12px 14px; font-size: 15px; border: 1.5px solid #e5e7eb; border-radius: 10px; outline: none; font-family: inherit; width: 100%; -webkit-appearance: none; }
  .reg-inp:focus { border-color: #2d2d7e; }
  .reg-inp.err { border-color: #ef4444; }
  .reg-err { font-size: 12px; color: #ef4444; margin-top: 2px; }
  .reg-btn { padding: 14px; font-size: 16px; font-weight: 600; background: #2d2d7e; color: #fff; border: none; border-radius: 10px; cursor: pointer; width: 100%; -webkit-appearance: none; }
  .reg-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .reg-link { color: #2d2d7e; text-decoration: none; font-weight: 600; }
  .type-card { border: 2px solid #e5e7eb; border-radius: 12px; padding: 18px 20px; cursor: pointer; transition: all .15s; display: flex; align-items: center; gap: 14px; }
  .type-card:hover { border-color: #2d2d7e; background: #f8f8ff; }
  .type-card.selected { border-color: #2d2d7e; background: #eef2ff; }
  @media (max-width: 599px) {
    .reg-page { padding: 0; }
    .reg-card { border-radius: 0; padding: 24px 16px; min-height: 100vh; box-shadow: none; }
    .reg-btn { padding: 16px; }
  }
`;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { saveVerifiedSession } = useAuth();

  const [step, setStep]               = useState(1); // 1 = tip+email+parola, 2 = OTP
  const [tip, setTip]                 = useState(searchParams.get('tip') === 'juridica' ? 'juridica' : searchParams.get('tip') === 'fizica' ? 'fizica' : '');
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [terms, setTerms]             = useState(false);
  const [userId, setUserId]           = useState('');
  const [otp, setOtp]                 = useState('');
  const [loading, setLoading]         = useState(false);
  const [errors, setErrors]           = useState({});

  function validate() {
    const e = {};
    if (!tip)           e.tip = 'Alegeți tipul de cont';
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Email invalid';
    if (!password || password.length < 8) e.password = 'Minim 8 caractere';
    else if (!/(?=.*[A-Z])(?=.*\d)/.test(password)) e.password = 'Necesită o majusculă și o cifră';
    if (confirmPass !== password) e.confirmPass = 'Parolele nu coincid';
    if (!terms) e.terms = 'Trebuie să acceptați termenii';
    return e;
  }

  async function onSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      const res = await api.post('/auth/register', { email: email.trim().toLowerCase(), password, tip_persoana: tip });
      setUserId(res.data.userId);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Eroare la înregistrare');
    } finally {
      setLoading(false);
    }
  }

  async function onVerifyOtp(e) {
    e.preventDefault();
    if (otp.length !== 6) return toast.error('Codul trebuie să aibă 6 cifre');
    setLoading(true);
    try {
      const res = await authAPI.verifyEmail({ userId, otp });
      // verify-email returnează acum sesiunea
      const needsProfile = saveVerifiedSession(res.data);
      toast.success('Cont activat cu succes!');
      navigate(needsProfile ? '/complete-profile' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cod incorect sau expirat');
    } finally {
      setLoading(false);
    }
  }

  async function onResend() {
    try {
      await authAPI.resendConfirmation({ userId });
      toast.success('Cod nou trimis!');
      setOtp('');
    } catch { toast.error('Eroare la retrimis'); }
  }

  if (step === 2) return (
    <>
      <style>{css}</style>
      <div className="reg-page">
        <div className="reg-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📧</div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 8px' }}>Verifică emailul</h1>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 24px', lineHeight: '1.5' }}>
            Am trimis un cod de 6 cifre la <strong>{email}</strong>.<br/>
            Verificați și folderul Spam dacă nu îl găsiți.
          </p>
          <form onSubmit={onVerifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <input
              type="text" inputMode="numeric" maxLength={6} autoFocus
              className="reg-inp"
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="_ _ _ _ _ _"
              style={{ textAlign: 'center', fontSize: '28px', letterSpacing: '10px', fontFamily: 'monospace', fontWeight: '700' }}
            />
            <button type="submit" disabled={loading || otp.length !== 6} className="reg-btn">
              {loading ? 'Se verifică...' : 'Activează contul'}
            </button>
          </form>
          <p style={{ fontSize: '13px', color: '#9ca3af', margin: '20px 0 8px' }}>
            Nu ai primit codul?{' '}
            <button onClick={onResend} style={{ background: 'none', border: 'none', color: '#2d2d7e', cursor: 'pointer', fontWeight: '600', fontSize: '13px', padding: 0 }}>
              Retrimite codul
            </button>
          </p>
          <Link to="/login" style={{ fontSize: '13px', color: '#9ca3af', textDecoration: 'none' }}>← Înapoi la login</Link>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      <div className="reg-page">
        <div className="reg-card">
          <div style={{ fontSize: '36px', textAlign: 'center', marginBottom: '12px' }}>🏛</div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 6px', textAlign: 'center' }}>Creează cont</h1>
          <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', margin: '0 0 28px', lineHeight: '1.5' }}>
            Înregistrați-vă pentru a depune cereri și sesizări online
          </p>

          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

            {/* Tip cont */}
            <div className="reg-fld">
              <label className="reg-lbl">Tip cont *</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div className={`type-card ${tip === 'fizica' ? 'selected' : ''}`} onClick={() => setTip('fizica')}>
                  <span style={{ fontSize: '28px' }}>👤</span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>Persoană fizică</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Cetățean individual</div>
                  </div>
                </div>
                <div className={`type-card ${tip === 'juridica' ? 'selected' : ''}`} onClick={() => setTip('juridica')}>
                  <span style={{ fontSize: '28px' }}>🏢</span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>Persoană juridică</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Firmă sau ONG</div>
                  </div>
                </div>
              </div>
              {errors.tip && <span className="reg-err">{errors.tip}</span>}
            </div>

            {/* Email */}
            <div className="reg-fld">
              <label className="reg-lbl">Adresă email *</label>
              <input type="email" className={`reg-inp${errors.email ? ' err' : ''}`}
                placeholder="exemplu@email.ro" value={email}
                onChange={e => setEmail(e.target.value)} autoComplete="email" />
              {errors.email && <span className="reg-err">{errors.email}</span>}
            </div>

            {/* Parolă */}
            <div className="reg-fld">
              <label className="reg-lbl">Parolă * <span style={{ fontWeight: 400, color: '#9ca3af' }}>(min. 8 caractere, 1 majusculă, 1 cifră)</span></label>
              <PasswordInput className={`reg-inp${errors.password ? ' err' : ''}`}
                placeholder="••••••••" value={password}
                onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
              {errors.password && <span className="reg-err">{errors.password}</span>}
            </div>

            <div className="reg-fld">
              <label className="reg-lbl">Confirmă parola *</label>
              <PasswordInput className={`reg-inp${errors.confirmPass ? ' err' : ''}`}
                placeholder="••••••••" value={confirmPass}
                onChange={e => setConfirmPass(e.target.value)} autoComplete="new-password" />
              {errors.confirmPass && <span className="reg-err">{errors.confirmPass}</span>}
            </div>

            {/* Termeni */}
            <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                <input type="checkbox" style={{ marginTop: '3px', flexShrink: 0 }}
                  checked={terms} onChange={e => setTerms(e.target.checked)} />
                <span>Sunt de acord cu{' '}
                  <a href="/termeni" style={{ color: '#2d2d7e', textDecoration: 'none' }} target="_blank">Termenii și condițiile</a>
                  {' '}și{' '}
                  <a href="/confidentialitate" style={{ color: '#2d2d7e', textDecoration: 'none' }} target="_blank">Politica de confidențialitate (GDPR)</a>
                </span>
              </label>
              {errors.terms && <span className="reg-err" style={{ display: 'block', marginTop: '4px' }}>{errors.terms}</span>}
            </div>

            <button type="submit" disabled={loading} className="reg-btn">
              {loading ? 'Se creează contul...' : 'Continuă →'}
            </button>
          </form>

          <p style={{ textAlign: 'center', fontSize: '14px', color: '#6b7280', marginTop: '20px' }}>
            Ai deja cont? <Link to="/login" className="reg-link">Autentifică-te</Link>
          </p>
        </div>
      </div>
    </>
  );
}
